import { Box, BoxProps, Loader, Spacer, TextNew } from '@jfs/ui-new'
import { createNavigationContainerRef, DefaultTheme, NavigationContainer } from '@react-navigation/native'
import { createStackNavigator } from '@react-navigation/stack'
import { lazy, Suspense, useEffect, useRef } from 'react'
import { ViewProps } from 'react-native'
import { Toast, ToastPosition, ToastType } from '../../comps/Toast'
import { loginStrings } from '../../data/static/strings'
import { useStores } from '../../data/store'
import { getFromStore, safelyParseJSON, setIsSmallDevice, useWindowDimensions } from '../../util/util'
import Dashboard from '../DashboardStack'
import Linking from '../Linking'
import LoginNav from '../LoginNavStack'
import { Routes } from '../routes'

const Success = lazy(() => import('../../screens/Success/'))
const Support = lazy(() => import('../../screens/Support/'))
//Code splitting for QR agent portal nav.
const QrAgentPortalNav = lazy(() => import('../QrAgentPortalNav'))

const MyTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: 'transparent',
  },
}

const Stack = createStackNavigator()
export const navigationRef: any = createNavigationContainerRef()
const { REACT_APP_ENDPOINT = '', REACT_APP_CHATBOT_URL = '' } = (window?._env_ ?? {})

const AppStack = () => {
  const viewPortWidth = useWindowDimensions()
  const {
    init,
    errorMessage,
    resetMessage,
    updateMobile,
    setAppLockEnabled,
    setAppLockLater,
    updateSession,
    setBgColor,
    resetCustomToast,
    isSetAppLockLater,
    updateEmailId,
  } = useStores((store) => ({
    init: store?.init,
    setBgColor: store?.response?.setBgColor,
    resetCustomToast: store?.response?.resetCustomToast,
    errorMessage: store?.response?.getMessage(),
    resetMessage: store?.response?.resetMessage,
    updateMobile: store.settings.metaData.updateMobileNumber,
    setAppLockEnabled: store.settings.metaData.setAppLockEnabled,
    setAppLockLater: store.settings.metaData.setAppLockLater,
    updateSession: store?.user?.session?.updateSessionData,
    isSetAppLockLater: store.settings.metaData.isSetAppLockLater,
    updateEmailId: store?.user?.session?.updateEmailId,
  }))
  const routeNameRef = useRef<any>()

  let deeplinkPath = null

  const getInitialRouteName = () => {
    const sessData = window?.localStorage.getItem('sessionData')
    const jfsUserId = window?.localStorage.getItem('jfsUserId')
    const pathName = window.location.pathname?.toLowerCase()
    const isMerchantPortalLink = !pathName?.includes('agent')

    if ((!sessData || sessData?.length === 0) && isMerchantPortalLink) {
      if (window.location.pathname?.length > 1 && window.location.pathname !== `${REACT_APP_ENDPOINT}/login`) {
        deeplinkPath = window.location.pathname?.slice(1)
      } else {
        deeplinkPath = null
      }
    } else {
      deeplinkPath =
        window.location.pathname?.length > 1 && window.location.pathname !== `${REACT_APP_ENDPOINT}/login`
          ? window.location.pathname?.slice(1)
          : null
    }
    if (REACT_APP_ENDPOINT && deeplinkPath?.startsWith(REACT_APP_ENDPOINT.slice(1))) {
      deeplinkPath = deeplinkPath?.slice(REACT_APP_ENDPOINT.length) ?? null
    }
    if (deeplinkPath?.startsWith(Routes.SUPERSET_DASHBOARD)) {
      deeplinkPath = deeplinkPath + window.location.search
    }

    if (sessData && sessData?.length > 0 && jfsUserId && isMerchantPortalLink) {
      return Routes.ROOTSCREEN
    } else if (!isMerchantPortalLink) {
      return Routes.QR_AGENT_PORTAL
    } else {
      return Routes.LOGIN
    }
  }

  const getFromLocal = () => {
    getFromStore(loginStrings.userMobileNumber).then((value) => {
      if (value !== null) {
        const jsonValue = JSON.parse(value)
        updateMobile(jsonValue)
      }
    })
    getFromStore('userEmailId').then((emailId) => {
      if (emailId?.length > 0) {
        updateEmailId(emailId)
      }
    })
    getFromStore('lockEnabled').then((value) => {
      if (value !== null) {
        const jsonValue = JSON.parse(value)
        setAppLockEnabled(jsonValue === 'true')
      } else {
        setAppLockEnabled(false)
      }
    })

    getFromStore('enableLockLater').then((value) => {
      if (value !== null) {
        const jsonValue = JSON.parse(value)
        setAppLockLater(jsonValue === 'true')
      } else {
        setAppLockLater(false)
      }
    })

    const sData = window?.localStorage.getItem('sessionData')
    const jfsUserId = window?.localStorage.getItem('jfsUserId')
    if (sData !== null && sData.length > 0 && jfsUserId) {
      let jsonValue: any = {}
      try {
        jsonValue = safelyParseJSON(sData)
        jsonValue.jfsUserId = jfsUserId
        updateSession(jsonValue)
      } catch (e) {}
    }
  }

  const embedChatBot = () => {
    const script = document.createElement('script')
    script.id = 'chatBotScript'
    script.src = REACT_APP_CHATBOT_URL
    document.head.appendChild(script)
  }

  useEffect(() => {
    init()
    getFromLocal()
    embedChatBot()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isSmallDevice = viewPortWidth < 550
  setIsSmallDevice(isSmallDevice)
  const initialRouteName = getInitialRouteName()
  return (
    <Box {...styles.container}>
      <Toast
        message={errorMessage.message}
        bgColor={errorMessage.bgColor}
        hide={errorMessage.message === ''}
        position={
          routeNameRef?.current === Routes.BULK_REFUND
            ? ToastPosition.TOP
            : errorMessage?.position ?? ToastPosition.BOTTOM
        }
        type={errorMessage?.toastType ?? ToastType.NEGATIVE}
        onDismiss={() => {
          resetMessage()
          setBgColor('')
          resetCustomToast('')
        }}
        timeOut={5000}
        minWidthOfLeftBox={errorMessage?.minWidthOfLeftBox}
      />
      <Suspense fallback={renderLoaderView(initialRouteName, isSmallDevice)}>
        <NavigationContainer
          theme={MyTheme}
          ref={navigationRef}
          linking={Linking}
          onReady={() => (routeNameRef.current = navigationRef?.getCurrentRoute()?.name)}
          independent={true}
          onStateChange={async () => {
            const previousRouteName = routeNameRef.current
            const currentRouteName = navigationRef?.getCurrentRoute()?.name
            if (previousRouteName !== currentRouteName) {
              // console.log('currentRouteName', currentRouteName)
            }
            routeNameRef.current = currentRouteName
          }}>
          <Stack.Navigator
            screenOptions={{
              headerBackTitleVisible: false,
              headerMode: 'screen',
              headerShown: isSmallDevice ? true : false,
            }}
            initialRouteName={initialRouteName}>
            <Stack.Screen
              name={Routes.LOGIN}
              component={LoginNav}
              options={{ headerShown: false }}
              initialParams={{ navigateTo: deeplinkPath }}
            />
            {/* Dashboard Stack */}
            <Stack.Screen
              name={Routes.ROOTSCREEN}
              component={Dashboard}
              options={{ headerShown: false }}
              initialParams={{ path: deeplinkPath }}
            />
            <Stack.Screen name={Routes.SUCCESS} component={Success} options={{ headerShown: false }} />
            <Stack.Screen name={Routes.CONTACT_US_LOGIN} component={Support} options={{ headerShown: false }} />
            <Stack.Screen name={Routes.QR_AGENT_PORTAL} component={QrAgentPortalNav} options={{ headerShown: false }} />
          </Stack.Navigator>
        </NavigationContainer>
      </Suspense>
    </Box>
  )
}

const overRideNav = (breakpoint: number) => {
  if (breakpoint < 850) {
    return {
      marginLeft: -220,
    } as ViewProps
  }
}

const styles = {
  container: {
    flex: 1,
  } as BoxProps,
  fallbackContainer: {
    height: '100vh',
    minHeight: 460,
    flexDirection: 'row',
    flex: 1,
  } as BoxProps,
  gradientBgStyle: (breakPointValue: number) =>
    ({
      width: 220,
      height: '100%',
      ...overRideNav(breakPointValue),
    } as ViewProps),
}

export { AppStack }

const renderLoaderView = (initialRouteName: string, isSmallDevice = false) => {
  const isLoggedIn = initialRouteName === Routes.ROOTSCREEN
  return (
    <Box flex={1} justify="center" bg={isSmallDevice ? 'SECONDARY' : isLoggedIn ? 'WHITE' : 'BLACK'}>
      <Loader type="large" color={isLoggedIn ? (isSmallDevice ? 'WHITE' : 'BLUE') : 'WHITE'} />
      <Spacer size={20} />
      <TextNew
        textAlign="center"
        fontSize={14}
        title="Loading Data... "
        color={isLoggedIn ? (isSmallDevice ? 'WHITE' : 'BLUE') : 'WHITE'}
      />
    </Box>
  )
}
